import { useState } from "react";
import { Button } from "../components/ui/button";
import { ChevronRight, ChevronDown, Calendar, Trash2 } from "lucide-react";

const nodeTypeColors = {
  phase: "bg-primary/20 text-primary",
  area: "bg-secondary/20 text-secondary",
  zone: "bg-amber-500/20 text-amber-400",
  work_package: "bg-emerald-500/20 text-emerald-400",
  milestone: "bg-pink-500/20 text-pink-400",
};

// Non-recursive single node renderer
const SingleNode = ({ node, level, activities, onDeleteNode, expanded, onToggle }) => {
  const nodeActivities = activities.filter((a) => a.project_node_id === node.id);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="border-l border-border/50" style={{ marginLeft: level * 16 }}>
      <div className="flex items-center gap-2 p-3 rounded-lg hover:bg-muted/50 transition-colors">
        <button
          onClick={() => onToggle(node.id)}
          className="text-muted-foreground hover:text-foreground"
        >
          {hasChildren || nodeActivities.length > 0 ? (
            expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
          ) : (
            <div className="w-4" />
          )}
        </button>
        <span
          className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${
            nodeTypeColors[node.node_type] || "bg-muted text-muted-foreground"
          }`}
        >
          {node.node_type}
        </span>
        <span className="font-medium flex-1">{node.name}</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onDeleteNode(node.id)}
        >
          <Trash2 size={14} className="text-destructive" />
        </Button>
      </div>
      {expanded && nodeActivities.map((activity) => (
        <div
          key={activity.id}
          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:bg-muted/30 rounded"
          style={{ marginLeft: (level + 1) * 16 + 32 }}
        >
          <Calendar size={14} />
          <span className="flex-1">{activity.name}</span>
          <span className="text-xs font-mono">{activity.duration_hours_planned}h</span>
        </div>
      ))}
    </div>
  );
};

// Flatten tree to list for iterative rendering
const flattenTree = (nodes, level = 0, expandedIds) => {
  const result = [];
  for (const node of nodes) {
    result.push({ node, level, expanded: expandedIds.has(node.id) });
    if (expandedIds.has(node.id) && node.children && node.children.length > 0) {
      result.push(...flattenTree(node.children, level + 1, expandedIds));
    }
  }
  return result;
};

const NodeTreeView = ({ nodes, activities, onDeleteNode }) => {
  const [expandedIds, setExpandedIds] = useState(new Set(nodes.map(n => n.id)));

  const toggleExpanded = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const flatList = flattenTree(nodes, 0, expandedIds);

  return (
    <div className="space-y-1">
      {flatList.map(({ node, level, expanded }) => (
        <SingleNode
          key={node.id}
          node={node}
          level={level}
          activities={activities}
          onDeleteNode={onDeleteNode}
          expanded={expanded}
          onToggle={toggleExpanded}
        />
      ))}
    </div>
  );
};

export default NodeTreeView;
